import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useHistory } from 'react-router-dom';
import StepperComponent from './StepperComponent';
import SwitcherComponent from './SwitcherComponent';
import DocumentMessageContainer from '../../edit/general/documentMessages/components/DocumentMessageContainer';
import initialSteps from 'constants/initialSteps';
import { QuotationsStatus } from 'constants/defaultValues';
import {
  useNavigationStepData,
  useBackdrop,
  useServerErrorHandling,
  useAuthUserData,
  useSimpleError,
  useQueryWithSimpleErrorHandling,
  useOverRouterContextUtils,
  useDocumentId,
  useParams as useBehaviorParams
} from 'hooks';
import InsuranceDocumentHeader from './InsuranceDocumentHeader';
import DuplicationContainer from '../../duplication/components/DuplicationContainer';
import { useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { useMutation } from 'features/common/security/hooks';
import { UPDATE_DOCUMENT_STATE } from '../mutations';
import {
  DOCUMENT_ALLOWED_TRANSITIONS_QUERY,
  DOCUMENT_HEADER_QUERY,
  QUOTATIONS_STATUS
} from 'features/insuranceDocument/queries/insuranceDocumentQueries';
import { DOCUMENT_ACTIONS_VISIBILITY_QUERY } from 'features/serviceLogs/queries';
import StateTransitionReasonsContainer from 'features/stateTransitions/StateTransitionReasonsContainer';
import { useApolloClient, useReactiveVar } from '@apollo/client';
import InsuranceDataContext from '../insuranceDataContext';
import { useTranslation } from 'react-i18next';
import { quotationsStatusVar } from 'features/insuranceDocument/variables';
import { useTitle } from 'hooks/useTitle';

const InsuranceDocumentStepperContainer = ({ contextRef, documentHeaderData, setDocumentHeaderData }) => {
  const [activeStep, setActiveStep] = useState(0);
  const { hasRightToEditDocument } = useAuthUserData();
  const client = useApolloClient();
  const location = useLocation();
  const { t } = useTranslation();
  const history = useHistory();
  const { documentId, isNew } = useDocumentId();

  const { setContext, setProperties } = useOverRouterContextUtils(contextRef);

  const [openConfirmDuplication, setOpenConfirmDuplication] = useState(false);
  const [duplicationInProgress] = useChangeTrackingLens(false);
  const showError = useSimpleError();
  const [documentAllowedTransitions, setDocumentAllowedTransitions] = useState([]);
  const status = useReactiveVar(quotationsStatusVar);
  const { hideBackdrop } = useBackdrop();
  const [openStateTransitionModal, setOpenStateTransitionModal] = useState(false);
  const [allowedState, setAllowedState] = useState({});

  const { useGetNavigationStep, setNavigationStep } = useNavigationStepData();
  const documentNavigationStep = useGetNavigationStep(documentId);
  const [steps, setSteps] = useState(initialSteps);

  const [routeInitialized, setRouteInitialized] = useState(false);

  const editableControlsConfigJson = useBehaviorParams('EditableControlsConfiguration');
  const editableControlsConfig = useMemo(
    () => editableControlsConfigJson && JSON.parse(editableControlsConfigJson),
    [editableControlsConfigJson]
  );

  const { loading: documentAllowedTransitionsLoading } = useQueryWithSimpleErrorHandling(DOCUMENT_ALLOWED_TRANSITIONS_QUERY, {
    variables: {
      documentStateId: documentHeaderData.documentStateId,
      documentTypeId: documentHeaderData.documentTypeId
    },
    skip: !documentHeaderData.documentStateId || !documentHeaderData.documentTypeId,
    onCompleted: data => {
      setDocumentAllowedTransitions(data.documentAllowedTransitions?.allowedTransitions);
    }
  });

  const { data: documentActionsVisibilityData, refetch: refetchDocumentActionsVisibility } = useQueryWithSimpleErrorHandling(
    DOCUMENT_ACTIONS_VISIBILITY_QUERY,
    {
      variables: { documentId },
      skip: !documentId
    }
  );

  const { data: quotationsStatusData, refetch: refetchQuotationsStatus } = useQueryWithSimpleErrorHandling(QUOTATIONS_STATUS, {
    variables: { documentId },
    skip: !documentId,
    onCompleted: data => quotationsStatusVar(data.quotationsStatus),
    fetchPolicy: 'network-only'
  });

  const setTitle = useCallback(
    () =>
      t('NavBar.OfferTypeAndNumber', {
        type: documentHeaderData.insuranceTypeName,
        number: documentHeaderData.documentNumber
      }),
    [documentHeaderData, t]
  );

  useTitle(setTitle());

  const allowControlEdit = useCallback(
    ({ customValidationFunction, controlName, checkQuotationsStatus, withControlNameValidation } = {}) => {
      if (!controlName && withControlNameValidation) return false;

      if (!hasRightToEditDocument(documentHeaderData.documentTypeId, documentHeaderData.documentStateId)) return false;

      if (customValidationFunction && !customValidationFunction()) return false;

      if (
        checkQuotationsStatus &&
        checkQuotationsStatus?.processingStatus &&
        (status === QuotationsStatus.PROCESSING || status === QuotationsStatus.COMPUTING)
      )
        return false;

      if (checkQuotationsStatus && checkQuotationsStatus?.expiredStatus && status === QuotationsStatus.EXPIRED) return false;

      if (withControlNameValidation) {
        const offerTypeCode = documentHeaderData.offerTypeCode;
        const documentStateCode = documentHeaderData.documentStateCode;

        if (!editableControlsConfig) return false;

        const configByType = editableControlsConfig.find(x => x.OfferTypeCodes.includes(offerTypeCode));

        // if no specific configuration is made for current offer type, the control will be enabled based on previous logic
        if (!configByType) return true;

        const checkControlConfiguration = (controls, control) => {
          if (controls.some(x => x === control)) return true;
          const base = control.substr(0, control.lastIndexOf('.'));
          if (base !== control) return checkControlConfiguration(controls, base);
          return false;
        };

        if (configByType['_ALL']) if (checkControlConfiguration(configByType['_ALL'], controlName)) return true;

        const configByTypeAndState = configByType[documentStateCode];
        // if no specific configuration is made for current document state, the control will enabled based on previous logic
        if (!configByTypeAndState) return true;

        return checkControlConfiguration(configByTypeAndState, controlName);
      }
      return true;
    },
    [
      hasRightToEditDocument,
      documentHeaderData.documentTypeId,
      documentHeaderData.documentStateId,
      documentHeaderData.offerTypeCode,
      documentHeaderData.documentStateCode,
      editableControlsConfig,
      status
    ]
  );
  const offerTypeConfigurationJSON = useBehaviorParams('OfferTypeCodesConfiguration');
  const offerTypeCodes = useMemo(() => {
    if (offerTypeConfigurationJSON) return JSON.parse(offerTypeConfigurationJSON);
  }, [offerTypeConfigurationJSON]);

  const isExternalOffer = useMemo(() => {
    return (
      documentHeaderData?.offerTypeCode === offerTypeCodes?.Cession ||
      documentHeaderData?.offerTypeCode === offerTypeCodes?.Restructuring ||
      documentHeaderData?.offerTypeCode === offerTypeCodes?.FinancedOffer
    );
  }, [documentHeaderData, offerTypeCodes]);

  const globalReadOnly = useCallback(
    ({
      customValidationFunction,
      controlName,
      checkQuotationsStatus = { processingStatus: true, expiredStatus: false },
      hasAuthorization = null
    } = {}) => {
      return (
        !allowControlEdit({
          customValidationFunction,
          controlName,
          checkQuotationsStatus,
          withControlNameValidation: isExternalOffer
        }) ||
        (hasAuthorization !== null && !hasAuthorization) ||
        documentHeaderData?.generationInProgress
      );
    },
    [allowControlEdit, isExternalOffer, documentHeaderData?.generationInProgress]
  );

  const handleStepClick = useCallback(
    step => {
      if (!step || isNew) return;
      const route = step.route.replace('{id}', documentId);
      if (location.pathname === route) return;
      history.push(route);
    },
    [documentId, history, isNew, location]
  );

  const handleGetPreviousStep = useCallback(
    stepIndex => {
      stepIndex--;
      if (stepIndex < 0) {
        return null;
      }
      const step = steps[stepIndex];
      return step;
    },
    [steps]
  );

  const handleGetNextStep = useCallback(
    stepIndex => {
      if (stepIndex === -1) {
        return null;
      }
      stepIndex++;
      if (stepIndex >= steps.length) {
        return null;
      }

      const step = steps[stepIndex];
      return step;
    },
    [steps]
  );

  const previousStepDisabled = useCallback(() => {
    const step = handleGetPreviousStep(activeStep);
    return (step && step.disabled) || !step;
  }, [activeStep, handleGetPreviousStep]);

  const nextStepDisabled = useCallback(() => {
    const step = handleGetNextStep(activeStep);
    return (step && step.disabled) || !step || isNew;
  }, [activeStep, handleGetNextStep, isNew]);

  const handlePreviousStepClick = useCallback(() => {
    const step = handleGetPreviousStep(activeStep);
    handleStepClick(step);
  }, [activeStep, handleGetPreviousStep, handleStepClick]);

  const handleNextStepClick = useCallback(() => {
    const step = handleGetNextStep(activeStep);
    handleStepClick(step);
  }, [activeStep, handleGetNextStep, handleStepClick]);

  const [updateDocumentState] = useMutation(UPDATE_DOCUMENT_STATE, {
    onError: error => showError(error)
  });

  const handleDocumentStateChange = useCallback(
    documentStateId => {
      if (documentStateId === documentHeaderData.documentStateId) return;

      var state = documentAllowedTransitions.find(state => state.id === documentStateId);
      if (state.requestComment === true || state.requestReason === true) {
        setOpenStateTransitionModal(true);
        setAllowedState(state);
        return;
      }
      setDocumentHeaderData(prevState => ({ ...prevState, documentStateId: documentStateId }));
      updateDocumentState({
        variables: {
          input: { documentId, documentStateId }
        }
      });
    },
    [documentHeaderData.documentStateId, documentId, updateDocumentState, documentAllowedTransitions, setDocumentHeaderData]
  );

  const handleSaveTransitionReason = useCallback(
    (documentId, documentStateId, comment, reasonIds) => {
      updateDocumentState({
        variables: {
          input: { documentId, documentStateId, comment, reasonIds }
        }
      });
      setOpenStateTransitionModal(false);
    },
    [updateDocumentState]
  );

  useEffect(() => {
    setSteps(
      initialSteps.map(step => ({
        ...step,
        disabled: step.index > documentNavigationStep.index
      }))
    );
  }, [documentNavigationStep.index]);

  useEffect(() => {
    const step = steps.find(a => location.pathname.toLowerCase() === a.route.toLowerCase().replace('{id}', documentId));
    if (step && step.index <= documentNavigationStep.index) {
      setActiveStep(step.index);
      setRouteInitialized(true);
    } else {
      const defaultInsuranceDocumentRoute = `/insurance-documents/${documentId}`;
      if (location.pathname.toLowerCase() === defaultInsuranceDocumentRoute || step) {
        if (documentNavigationStep.code !== null && documentNavigationStep.code !== undefined && !isNew) {
          const navigationStep = steps.find(a => a.code === documentNavigationStep.code);
          const route = navigationStep.route.replace('{id}', documentId);
          setActiveStep(documentNavigationStep.index);
          setRouteInitialized(true);
          history.push(route);
        }
      } else {
        setActiveStep(-1);
        setRouteInitialized(true);
      }
    }
  }, [documentId, documentNavigationStep.code, documentNavigationStep.index, history, isNew, location.pathname, steps]);

  const handleNavigationStepChanged = data => {
    const changedNavigationStepCode = data.navigationStepId;
    setNavigationStep(documentId, changedNavigationStepCode);

    if (changedNavigationStepCode && !data.silentMode) {
      const step = steps.find(x => x.code === changedNavigationStepCode);
      const route = step.route.replace('{id}', documentId);
      hideBackdrop();
      history.push(route);
    }
  };

  const handleDocumentStateChangeFailed = data => {
    setDocumentHeaderData(prevState => ({
      ...prevState,
      documentStateId: data.changeDocumentStateFailed.oldStateId
    }));
  };

  const { storeKey: generatePolicyErrorHandling } = useServerErrorHandling({
    effect: _ => {
      const { onPolicyGenerationError } = contextRef.current.events;
      onPolicyGenerationError && onPolicyGenerationError();
    },
    persistence: 'apollo'
  });

  setContext(
    {
      onChangeDocumentStateFailed: [...contextRef.current.events.onChangeDocumentStateFailed, handleDocumentStateChangeFailed],
      onDocumentStateUpdated: data => {
        const { documentAllowedTransitions } = data;
        setDocumentAllowedTransitions(documentAllowedTransitions);
        setDocumentHeaderData(prevState => ({
          ...prevState,
          documentStateId: data.documentStateId,
          documentStateCode: data.documentStateCode,
          documentStateName: data.documentStateName
        }));
        client.writeQuery({
          query: DOCUMENT_ALLOWED_TRANSITIONS_QUERY,
          data: {
            documentAllowedTransitions: {
              __typename: 'DocumentAllowedTransitions',
              documentStateId: data.documentStateId,
              documentTypeId: documentHeaderData.documentTypeId,
              allowedTransitions: documentAllowedTransitions
            }
          },
          variables: {
            documentStateId: data.documentStateId,
            documentTypeId: documentHeaderData.documentTypeId
          }
        });

        const { documentHeader } = client.readQuery({
          query: DOCUMENT_HEADER_QUERY,
          variables: { documentId }
        });

        client.writeQuery({
          query: DOCUMENT_HEADER_QUERY,
          data: {
            documentHeader: {
              __typename: 'DocumentHeader',
              ...documentHeader,
              documentStateId: data.documentStateId,
              documentStateCode: data.documentStateCode,
              documentStateName: data.documentStateName
            }
          },
          variables: {
            documentId
          }
        });

        client.cache.gc();
      },
      onNavigationStepChanged: handleNavigationStepChanged,
      onQuotationsStatusChanged: () => {
        if (documentId) {
          refetchDocumentActionsVisibility({ documentId });
          refetchQuotationsStatus({ documentId });
        }
      },
      onGenerationStatusChanged: generationInProgress => {
        setDocumentHeaderData(prevState => ({
          ...prevState,
          generationInProgress: generationInProgress
        }));

        const { documentHeader } = client.readQuery({
          query: DOCUMENT_HEADER_QUERY,
          variables: { documentId }
        });

        client.writeQuery({
          query: DOCUMENT_HEADER_QUERY,
          data: {
            documentHeader: {
              __typename: 'DocumentHeader',
              ...documentHeader,
              generationInProgress: generationInProgress
            }
          },
          variables: {
            documentId
          }
        });

        client.cache.gc();
      }
    },
    { generatePolicyErrorHandling }
  );

  setProperties({ quotationsStatus: quotationsStatusData?.quotationsStatus });

  const insuranceDocumentData = useMemo(
    () => ({
      globalReadOnly,
      documentStateId: documentHeaderData.documentStateId,
      documentTypeId: documentHeaderData.documentTypeId,
      documentAllowedTransitions
    }),
    [globalReadOnly, documentHeaderData, documentAllowedTransitions]
  );

  return (
    <InsuranceDataContext.Provider value={insuranceDocumentData}>
      <InsuranceDocumentHeader
        isNew={isNew}
        documentId={documentId}
        onConfirmDuplication={() => setOpenConfirmDuplication(true)}
        duplicationInProgress={duplicationInProgress}
        documentHeaderData={documentHeaderData}
        onChangeDocumentState={handleDocumentStateChange}
        documentAllowedTransitions={documentAllowedTransitions}
        documentAllowedTransitionsLoading={documentAllowedTransitionsLoading}
        actionsVisibility={documentActionsVisibilityData?.documentActionsVisibility}
      />
      {documentId && (
        <DuplicationContainer
          openConfirmDuplication={openConfirmDuplication}
          onCloseConfirmationDialog={() => setOpenConfirmDuplication(false)}
          documentId={documentId}
          duplicationInProgress={duplicationInProgress}
        />
      )}
      <StepperComponent
        steps={steps}
        activeStep={activeStep}
        onStepClick={handleStepClick}
        onPreviousStepClick={handlePreviousStepClick}
        previousStepDisabled={previousStepDisabled}
        onNextStepClick={handleNextStepClick}
        nextStepDisabled={nextStepDisabled}
      />
      {documentId && <DocumentMessageContainer documentId={documentId} contextRef={contextRef} />}
      {routeInitialized && globalReadOnly && (
        <SwitcherComponent
          document={{ isNew, documentId, currentStateCode: documentHeaderData?.documentStateCode }}
          contextRef={contextRef}
        />
      )}
      {openStateTransitionModal && (
        <StateTransitionReasonsContainer
          documentId={documentId}
          transitionConfig={allowedState}
          onSave={handleSaveTransitionReason}
          onCloseModal={() => setOpenStateTransitionModal(false)}
        />
      )}
    </InsuranceDataContext.Provider>
  );
};

InsuranceDocumentStepperContainer.propTypes = {
  contextRef: PropTypes.shape({
    current: PropTypes.shape({
      properties: PropTypes.object.isRequired,
      events: PropTypes.shape({
        onChangeDocumentStateFailed: PropTypes.array.isRequired,
        onDocumentStateUpdated: PropTypes.func.isRequired,
        onPolicyGenerationError: PropTypes.func,
        onNavigationStepChanged: PropTypes.func.isRequired
      }),
      functions: PropTypes.object.isRequired
    })
  }),
  documentHeaderData: PropTypes.object.isRequired,
  setDocumentHeaderData: PropTypes.func.isRequired
};

export default InsuranceDocumentStepperContainer;
